import { FC, useState, useEffect, useMemo } from "react"
import { FormikProps, Form, getIn, FieldArray } from "formik"

import {
  FormikTextField,
  FormikFileUpload,
  StatusErrorMessage,
  FileUploaderResponse,
  FormikAcknowledgeFeeCheck,
  FormikNotesArea
} from "../../../../../../components/widgets";

import { AttachmentCard } from "../../../../../core/attachments/AttachmentCard";

import { DLMLabelSelectorModal } from "../../../../../core/doclabel";

import { IDLMROIReplaceFormModel } from "./_models";
import { filter } from "lodash";
import { SubmitButtonRow } from "../../../../../../components/layout";

type Props = {
  doClose: Function
  resubmitMode?: string
}
const DLMROIReplaceModalForm: FC<FormikProps<IDLMROIReplaceFormModel> & Props> = ({status, isSubmitting, touched, errors, values, setFieldValue, setTouched, doClose, resubmitMode = ''}) => {

  const [ select_codes, setSelectCodes ] = useState<Array<string> | null>(null);
  const [ target_codes, setTargetCodes ] = useState<Array<string> | null>(null);
  const [ selected_series, setSelectedSeries] = useState(-1);

  const { labelDetails, otherAttachments } = values;

  const numOtherAttachments = useMemo(() => {
    return otherAttachments && otherAttachments.length > 0 ? otherAttachments.length : 0
  }, [otherAttachments]);

  const handleLabelSelect = (retval: Array<string>) => {
    if (! (selected_series > -1)) {
      return;
    }
    const _targetField = `labelDetails.${selected_series}.targetLabels`;
    setFieldValue(_targetField, retval && retval.length > 0 ? retval : null);
    setSelectedSeries(-1);
    setSelectCodes(null);
    setTouched({ hasTargets : true });
  }

  useEffect(()=> {
    if (! (labelDetails && labelDetails.length > 0)) {
      setFieldValue('hasTargets', 0);
      return;
    }
    setFieldValue('hasTargets', filter(labelDetails, o => (o.targetLabels !== null)).length , true);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [labelDetails]);

  return(
  <>
  <Form id='kt_label_replace_form' className='form' noValidate>
    <div className='fw-bold text-muted fs-6'>
      Label Series
    </div>
    <div className='separator border-gray-200 my-0'></div>
    {touched.hasTargets && errors.hasTargets && (
    <div className='fv-plugins-message-container'>
      <div className='fv-help-block'>
        <span role='alert'>{errors.hasTargets}</span>
      </div>
    </div>
    )}
    <div className='table-responsive min-h-80px mh-300px'>
      <table
        id='kt_table_roisplit_labels'
        className='table align-middle table-row-bordered table-row-gray-300 fs-7 gy-2 dataTable no-footer'
      >
      <thead>
        <tr className='text-start text-muted fw-bolder fs-7 text-uppercase gs-0'>
          <th className="p-2 w-125px">Series</th>
          <th className="p-2 w-100px">Select</th>
          <th className="p-2">Target Label</th>
        </tr>
      </thead>
      { (labelDetails && labelDetails.length > 0) &&
      <tbody className='text-gray-800 align-middle fs-6'>
        {
        [...Array(labelDetails.length)].map((_ , idx) => (
        <tr
          key={`labelDetail_${idx}`}
          className="labelRow"
          data-idx={idx}
        >
          <td className="p-2 align-top fs-7">
            <>
            {getIn(values, `labelDetails.${idx}.labelFrom`)}
            -
            {getIn(values, `labelDetails.${idx}.labelTo`)}
            </>
          </td>
          <td className="p-2 align-top">
            <button
              type="button"
              className="siteBtn btn btn-light btn-active-primary px-3 py-1 fs-7"
              onClick={() => {
                  setSelectedSeries(idx);
                  setSelectCodes(getIn(values,`labelDetails.${idx}.labelCodes`))
                  setTargetCodes(getIn(values,`labelDetails.${idx}.targetLabels`))
              }}
              title="Select Lost Labels"
            >
              Select
            </button>
            {
            // @ts-ignore
            (labelDetails[idx] && labelDetails[idx]?.targetLabels && labelDetails[idx].targetLabels.length > 0) &&
            <div className="d-inline-block text-muted fs-7 mt-1 ms-1">
              {/* @ts-ignore */}
              ({labelDetails[idx].targetLabels.length})
            </div>
            }
          </td>
          <td className="p-2 align-top fs-7">
            {
            // @ts-ignore
            (labelDetails[idx] && labelDetails[idx]?.targetLabels && labelDetails[idx].targetLabels.length > 0) ? (
              getIn(values, `labelDetails.${idx}.targetLabels`).join(' | ')
            ) : (
              <span className="text-muted fst-italic">(no selection)</span>
            )
            }
          </td>
        </tr>
        ))
        }
      </tbody>
      }
      </table>
    </div>
    <div className='separator border-gray-200 my-2'></div>
    <div className="mb-2 row">
      <FormikFileUpload size="6" label={`Other Attachments${numOtherAttachments > 0 ? ' [' + numOtherAttachments + ']' : ''}`}     name="otherAttachments" acceptFiles="ApplicantFileFormats" isFileArray={true} />
      <FormikFileUpload size="6" label="Police Report" name="policeReport" acceptFiles="ApplicantFileFormats" required={true} />
    </div>
    <FieldArray
      name="otherAttachments"
      render={arrayHelpers => (
      <div className="d-flex flex-row flex-wrap gap-2 mh-80px overflow-auto">
      { (values.otherAttachments && values.otherAttachments.length > 0) &&
      values.otherAttachments.map( (o: FileUploaderResponse, idx: number) => (
        <AttachmentCard
          key={`otherAttachment${idx}`}
          className="border py-1"
          idx={idx}
          fileId={o.id}
          fileName={o.name}
          onRemove={(e: any) => {
            e.stopPropagation();
            const elem = e.target.closest('.attachment-card');
            if (elem) {
              const {idx} = elem.dataset;
              arrayHelpers.remove(idx);
            }
          }}
        />
      ))
      }
      </div>
      )}
    />

    <div className="row mb-2 mt-4">
      <FormikTextField size="12" label="Comments" name="comment" required={true} rows={3} />
    </div>

    { resubmitMode &&
    <>
    <div className='separator border-gray-200 my-4'></div>
    <FormikNotesArea />
    </>
    }

    <FormikAcknowledgeFeeCheck dlmtype="ROI"/>

    <StatusErrorMessage status={status} />
    <SubmitButtonRow cancelText="Discard" doClose={doClose} />
  </Form>

  { (selected_series > -1 && select_codes !== null) &&
  <DLMLabelSelectorModal
    title="Select Labels"
    subTitle="Select labels for replacement"
    labelCodes={select_codes}
    targetLabels={target_codes}
    doClose={() => {setSelectedSeries(-1); setSelectCodes(null);}}
    doSubmit={handleLabelSelect}
  />
  }  
  </>
  )
}

export { DLMROIReplaceModalForm }